import React, { useState, useEffect, useMemo } from 'react';
import { gql, useQuery } from '@apollo/client';
import './Dashboard.css';

// ----------------------- Requêtes GraphQL -----------------------
// Pièces perdues (inchangées, avec telephone déjà ajouté)
const Q_CIP = gql`
  query {
    carte_identite_perdue(order_by: { id: desc }) {
      nom prenom date_naissance lieu_naissance lieu_vote adresse telephone
    }
  }
`;
const Q_CEP = gql`
  query {
    carte_etudiant_perdue(order_by: { id: desc }) {
      ine ecole_rattachement nom prenom date_naissance lieu_naissance filiere telephone
    }
  }
`;
// Pièces ramassées avec ajout du champ telephone
const Q_CIR = gql`
  query {
    carteidentiteramassee(order_by: { id: desc }) {
      nom prenom date_naissance lieu_naissance lieu_vote adresse telephone etat
    }
  }
`;
const Q_CER = gql`
  query {
    carteetudiantramassee(order_by: { id: desc }) {
      ine ecole_rattachement nom prenom filiere date_naissance lieu_naissance telephone etat
    }
  }
`;
const Q_PR = gql`
  query {
    passeportramasse(order_by: { id: desc }) {
      nom prenom date_naissance lieu_naissance nationalite telephone etat
    }
  }
`;
const Q_PER = gql`
  query {
    permisramasse(order_by: { id: desc }) {
      nom prenom date_naissance lieu_naissance categories adresse telephone etat
    }
  }
`;
const Q_CCR = gql`
  query {
    cartecommerceramassee(order_by: { id: desc }) {
      nom prenom profession numero_agrement telephone etat id
    }
  }
`;
const Q_CPR = gql`
  query {
    carteprofessionnelleramassee(order_by: { id: desc }) {
      nom prenom matricule telephone etat id
    }
  }
`;
const Q_CGR = gql`
  query {
    cartegriseramassee(order_by: { id: desc }) {
      id nom prenom numero_plaque marque_modele telephone
    }
  }
`;
const Q_ADR = gql`
  query {
    autredocumentramasse(order_by: { id: desc }) {
      nom prenom type_document date_naissance lieu_naissance description telephone etat id
    }
  }
`;

// Pièces perdues déjà avec le champ telephone
const Q_CGP = gql`
  query {
    carte_grise_perdue(order_by: { id: desc }) {
      id nom prenom numero_plaque marque_modele telephone
    }
  }
`;
const Q_PP = gql`
  query {
    passeport_perdu(order_by: { id: desc }) {
      id nom prenom date_naissance lieu_naissance nationalite telephone
    }
  }
`;
const Q_PERMIS_PERDU = gql`
  query {
    permis_perdu(order_by: { id: desc }) {
      id nom prenom date_naissance lieu_naissance categories adresse telephone
    }
  }
`;
const Q_CARTE_COMMERCE_PERDUE = gql`
  query {
    carte_commerce_perdue(order_by: { id: desc }) {
      id nom prenom profession numero_agrement telephone
    }
  }
`;
const Q_CARTE_PROFESSIONNELLE_PERDUE = gql`
  query {
    carte_professionnelle_perdue(order_by: { id: desc }) {
      id nom prenom matricule telephone
    }
  }
`;
const Q_AUTRE_DOCUMENT_PERDU = gql`
  query {
    autre_document_perdu(order_by: { id: desc }) {
      id nom prenom type_document date_naissance lieu_naissance description telephone
    }
  }
`;

const Q_ADMIN = gql`
  query {
    admins {
      nom prenom
    }
  }
`;

// ----------------------- Utilitaires -----------------------
const labelize = key => key ? key.replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase()) : '';

// ----------------------- Composant AutoSection -----------------------
const AutoSection = ({ title, rows, highlight }) => {
  const safeRows = Array.isArray(rows) ? rows : [];
  const columns = useMemo(() => {
    const keys = new Set();
    safeRows.forEach(row =>
      Object.keys(row || {}).filter(k => k !== 'id' && k !== '__typename').forEach(k => keys.add(k))
    );
    return Array.from(keys).sort();
  }, [safeRows]);

  return (
    <section style={{
      marginTop: 24,
      padding: highlight ? 16 : 0,
      borderRadius: highlight ? 8 : 0,
      backgroundColor: highlight ? '#e0f0ff' : 'transparent',
      boxShadow: highlight ? '0 0 8px rgba(0,123,255,0.3)' : 'none',
    }}>
      <h3>{title}</h3>
      <div style={{ overflowX: 'auto' }}>
        <table>
          <thead>
            <tr>
              {columns.map(col => (
                <th key={col} style={{ textAlign: 'left', borderBottom: '1px solid #ddd', padding: '8px', whiteSpace: 'nowrap' }}>
                  {labelize(col)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {safeRows.length === 0 ? (
              <tr>
                <td colSpan={columns.length || 1} style={{ padding: '12px', color: '#666' }}>Aucune donnée</td>
              </tr>
            ) : (
              safeRows.map((row, idx) => (
                <tr key={idx}>
                  {columns.map(col => (
                    <td key={col} style={{ padding: '8px', borderBottom: '1px solid #f1f1f1' }}>
                      {row[col] == null ? '' : String(row[col])}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
};

// ----------------------- Calcul taux -----------------------
function calculerTauxRessemblance(perdu, ramasse) {
  if (!perdu || !ramasse) return 0;
  const champs = [
    'nom','prenom','date_naissance','lieu_naissance','lieu_vote','adresse',
    'filiere','categories','numero_plaque','marque_modele','profession',
    'matricule','ine','ecole_rattachement','telephone'
  ];
  let score = 0, total = 0;
  champs.forEach(champ => {
    if (perdu[champ] != null && ramasse[champ] != null) {
      total++;
      if (String(perdu[champ]).toLowerCase() === String(ramasse[champ]).toLowerCase()) score++;
    }
  });
  return total === 0 ? 0 : (score / total) * 100;
}

// ----------------------- Composant Seuil -----------------------
const Seuil = ({ data }) => {
  if (!data || data.length === 0) return null;

  const getScoreClass = score =>
    score >= 80 ? 'high' : score >= 60 ? 'medium' : 'low';

  const ramasseKeys = Array.from(new Set(data.flatMap(item => Object.keys(item.ramasse || {}))))
    .filter(k => k !== '__typename');

  const perduKeys = Array.from(new Set(data.flatMap(item => Object.keys(item.perdu || {}))))
    .filter(k => k !== '__typename');

  return (
    <table className="seuil-table">
      <thead>
        <tr>
          <th>Ramassée</th>
          <th>Perdue</th>
          <th>Taux de ressemblance</th>
        </tr>
      </thead>
      <tbody>
        {data.map((item, index) => (
          <tr key={index}>
            <td>{ramasseKeys.map((key, i) => <span key={i}>{item.ramasse[key]} </span>)}</td>
            <td>{perduKeys.map((key, i) => <span key={i}>{item.perdu[key]} </span>)}</td>
            <td><span className={`seuil-score ${getScoreClass(item.taux)}`}>{item.taux.toFixed(0)}%</span></td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

// ----------------------- Composant Dashboard -----------------------
const Dashboard = () => {
  const [section, setSection] = useState('perdue');
  const [comparaisons, setComparaisons] = useState([]);

  const { data: cip } = useQuery(Q_CIP, { pollInterval: 3000 });
  const { data: cep } = useQuery(Q_CEP, { pollInterval: 3000 });
  const { data: cir } = useQuery(Q_CIR, { pollInterval: 3000 });
  const { data: cer } = useQuery(Q_CER, { pollInterval: 3000 });
  const { data: pr } = useQuery(Q_PR, { pollInterval: 3000 });
  const { data: per } = useQuery(Q_PER, { pollInterval: 3000 });
  const { data: ccr } = useQuery(Q_CCR, { pollInterval: 3000 });
  const { data: cpr } = useQuery(Q_CPR, { pollInterval: 3000 });
  const { data: cgr } = useQuery(Q_CGR, { pollInterval: 3000 });
  const { data: adr } = useQuery(Q_ADR, { pollInterval: 3000 });
  const { data: cgp } = useQuery(Q_CGP, { pollInterval: 3000 });
  const { data: pp } = useQuery(Q_PP, { pollInterval: 3000 });
  const { data: ppPerdu } = useQuery(Q_PERMIS_PERDU, { pollInterval: 3000 });
  const { data: ccpPerdue } = useQuery(Q_CARTE_COMMERCE_PERDUE, { pollInterval: 3000 });
  const { data: cppPerdue } = useQuery(Q_CARTE_PROFESSIONNELLE_PERDUE, { pollInterval: 3000 });
  const { data: adpPerdue } = useQuery(Q_AUTRE_DOCUMENT_PERDU, { pollInterval: 3000 });
  const { data: adminData, loading: adminLoading, error: adminError } = useQuery(Q_ADMIN, { pollInterval: 3000 });

  // ----------------------- Comparaison seuil ≥ 60% -----------------------
  useEffect(() => {
    if (!cip?.carte_identite_perdue || !cir?.carteidentiteramassee) return;
    const result = [];
    cip.carte_identite_perdue.forEach(perdu => {
      cir.carteidentiteramassee.forEach(ramasse => {
        const taux = calculerTauxRessemblance(perdu, ramasse);
        if (taux >= 60) result.push({ perdu, ramasse, taux });
      });
    });
    // Tri décroissant sur le taux
    result.sort((a, b) => b.taux - a.taux);
    setComparaisons(result);
  }, [cip, cir]);

  const isTrouvee = etat =>
    String(etat || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase() === 'retrouvee';
  const filterEtat = rows => Array.isArray(rows) ? rows.filter(r => isTrouvee(r?.etat)) : [];

  return (
    <div className="dashboard">
      <h1>
        {adminLoading
          ? 'Chargement...'
          : adminError
            ? 'Erreur de chargement utilisateur'
            : adminData?.admins?.length > 0
              ? `${adminData.admins[0].nom} ${adminData.admins[0].prenom}`
              : 'Utilisateur non trouvé'}
      </h1>

      <h1>Tableau de bord Administrateur</h1>

      <div style={{ marginBottom: 16 }}>
        {['perdue', 'ramassee', 'trouvees', 'seuil'].map(s => (
          <button key={s} onClick={() => setSection(s)} className={section === s ? 'btn-active' : 'btn-inactive'}>
            {s === 'perdue' && 'Pièces perdues'}
            {s === 'ramassee' && 'Pièces ramassées'}
            {s === 'trouvees' && 'Pièces trouvées'}
            {s === 'seuil' && 'Seuil de ressemblance'}
          </button>
        ))}
      </div>

      {section === 'perdue' && <>
        <h2>Pièces perdues</h2>
        <AutoSection title="Cartes d'identités" rows={cip?.carte_identite_perdue || []} highlight />
        <AutoSection title="Cartes étudiants" rows={cep?.carte_etudiant_perdue || []} highlight />
        <AutoSection title="Passeports" rows={pp?.passeport_perdu || []} highlight />
        <AutoSection title="Permis" rows={ppPerdu?.permis_perdu || []} highlight />
        <AutoSection title="Cartes commerces" rows={ccpPerdue?.carte_commerce_perdue || []} highlight />
        <AutoSection title="Cartes professionelles" rows={cppPerdue?.carte_professionnelle_perdue || []} highlight />
        <AutoSection title="Cartes grises" rows={cgp?.carte_grise_perdue || []} highlight />
        <AutoSection title="Autre documents" rows={adpPerdue?.autre_document_perdu || []} highlight />
      </>}

      {section === 'ramassee' && <>
        <h2>Pièces ramassées</h2>
        <AutoSection title="Cartes d'identité" rows={cir?.carteidentiteramassee || []} highlight />
        <AutoSection title="Cartes étudiant" rows={cer?.carteetudiantramassee || []} highlight />
        <AutoSection title="Passeports" rows={pr?.passeportramasse || []} highlight />
        <AutoSection title="Permis" rows={per?.permisramasse || []} highlight />
        <AutoSection title="Cartes de commerce" rows={ccr?.cartecommerceramassee || []} highlight />
        <AutoSection title="Cartes professionnelles" rows={cpr?.carteprofessionnelleramassee || []} highlight />
        <AutoSection title="Cartes grises" rows={cgr?.cartegriseramassee || []} highlight />
        <AutoSection title="Autres documents" rows={adr?.autredocumentramasse || []} highlight />
      </>}

      {section === 'trouvees' && <>
        <h2>Pièces trouvées</h2>
        <AutoSection title="Cartes grises (perdues → retrouvées)" rows={filterEtat(cgp?.carte_grise_perdue)} highlight />
        <AutoSection title="Passeports (perdus → retrouvées)" rows={filterEtat(pp?.passeport_perdu)} />
        <AutoSection title="Cartes grises (trouvées)" rows={filterEtat(cgr?.cartegriseramassee)} highlight />
        <AutoSection title="Cartes d'identité (trouvées)" rows={filterEtat(cir?.carteidentiteramassee)} />
        <AutoSection title="Cartes étudiant (trouvées)" rows={filterEtat(cer?.carteetudiantramassee)} />
        <AutoSection title="Passeports (trouvés)" rows={filterEtat(pr?.passeportramasse)} />
        <AutoSection title="Permis (trouvés)" rows={filterEtat(per?.permisramasse)} />
        <AutoSection title="Cartes de commerce (trouvées)" rows={filterEtat(ccr?.cartecommerceramassee)} />
        <AutoSection title="Cartes professionnelles (trouvées)" rows={filterEtat(cpr?.carteprofessionnelleramassee)} />
        <AutoSection title="Autres documents (trouvés)" rows={filterEtat(adr?.autredocumentramasse)} />
      </>}

      {section === 'seuil' && <>
        <h2>Seuil de ressemblance (≥ 60%)</h2>
        <Seuil data={comparaisons} />
      </>}
    </div>
  );
};

export default Dashboard;